import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatCardModule } from '@angular/material/card';
import { MatSliderModule } from '@angular/material/slider';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { TagDropzoneComponent } from '../../../../../shared/components/tag-dropzone/tag-dropzone.component';
import {
	AutoTagClipsRequest,
	CreateCategoryDetailsRequest,
	CreateClipRequest,
	GenerateClipReportForTagRequest,
	ManagementAuthorControllerService,
	ManagementAuthorResponse,
	ManagementClipControllerService,
	ManagementClipResponse,
	ManagementMediumClippingControllerService,
	ManagementMediumResponse,
	ManagementScreenshotControllerService,
	ManagementTagResponse,
	MergeSimilarClipsRequest,
	SearchRequest,
	UpdateClipRequest,
} from 'api-clipping';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { startWith } from 'rxjs/internal/operators/startWith';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';
import { take } from 'rxjs/internal/operators/take';
import { UtilsService } from '../../../../../shared/services/utils.service';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { SharedService } from '../../../../../shared/services/shared.service';
import { MatTabsModule } from '@angular/material/tabs';
import { Constants } from '../../../../../shared/interfaces/constants';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FailedUpload, UploadManagementService } from '../../../../../shared/services/upload-management.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SplitButtonComponent } from '../../../../../shared/components/split-button/split-button.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { ManagementTranslationControllerService } from 'api-ai-hub';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TagsDropAssignService } from '../../../../../shared/services/tags-drop-assign.service';
import { CdkDropList } from '@angular/cdk/drag-drop';
import { ClipsToolsComponent } from '../clips-tools/clips-tools.component';
import { MediumComponent } from '../../../management/medium/medium.component';
import { AuthorComponent } from '../../../management/author/author.component';
import { ScreenshotQueueDialogComponent } from '../../../../../shared/components/screenshot-queue-dialog/screenshot-queue-dialog.component';

@Component({
	selector: 'app-clips-form',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatSelectModule,
		TranslateModule,
		MatDatepickerModule,
		MatInputModule,
		MatButtonModule,
		MatCheckboxModule,
		MatCardModule,
		MatSliderModule,
		MatIconModule,
		MatButtonToggleModule,
		TagDropzoneComponent,
		MatAutocompleteModule,
		MatDividerModule,
		MatChipsModule,
		MatTabsModule,
		MatProgressSpinnerModule,
		MatTooltipModule,
		CdkDropList,
		SplitButtonComponent,
	],
	templateUrl: './clips-form.component.html',
	styleUrl: './clips-form.component.scss',
})
export class ClipsFormComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
	private onDestroy: Subject<any> = new Subject();
	private componentStateChanged: Subject<any> = new Subject();

	@Input() itemForEdit: ManagementClipResponse | undefined;
	@Output() actionRequested: EventEmitter<any> = new EventEmitter();

	apiLoading = {
		clip: false,
		cluster: false,
		searchKeywords: false,
		translate: false,
		mergeBySimilarity: false,
		autoTag: false,
		reportByTag: false,
		importClips: false,
		translateClipsByTag: false,
	};

	mode: 'NEW' | 'EDIT' = 'NEW';

	assignedTags: ManagementTagResponse[] = [];

	avaliableLanguagesCodes = Constants.AVAILABLE_LANGUAGES.map(item => item.code);

	selectedTabIndex = 0;

	clusterFormGroup: FormGroup = new FormGroup({});
	clipFormGroup: FormGroup = new FormGroup({});

	@ViewChild('clipsFormComponent', { static: false }) clipsFormElement!: ElementRef;
	@ViewChild('clipTextArea', { static: false }) clipTextArea!: ElementRef<HTMLInputElement>;
	fullTextPreview = false;

	filteredMediums: ManagementMediumResponse[] | undefined;

	separatorKeysCodes: number[] = [ENTER, COMMA];
	@ViewChild('authorInput', { static: false }) authorInput!: ElementRef<HTMLInputElement>;
	// Ѓ2 - boundary player, same "Постави од плеерот" convenience as "Провери" (Ѓ1). Positioned
	// at the clip's own start boundary on load (onBoundaryPlayerLoadedMetadata), paused at its end
	// during playback (onBoundaryPlayerTimeUpdate) - a lightweight preview, not the full
	// zoom/trim UI ClipsDetailedViewComponent has for the client-facing download dialog.
	@ViewChild('boundaryPlayer', { static: false }) boundaryPlayerRef?: ElementRef<HTMLVideoElement>;
	private boundaryPlayerAutoStopped = false;
	filteredAuthors: ManagementAuthorResponse[] | undefined;
	selectedAuthors: ManagementAuthorResponse[] = [];

	plugins = {
		webClipPopulateFromClipboard: false,
	};

	constructor(
		private fb: FormBuilder,
		private cdr: ChangeDetectorRef,
		private apiClippingMedium: ManagementMediumClippingControllerService,
		private apiClippingAuthor: ManagementAuthorControllerService,
		private apiClippingClips: ManagementClipControllerService,
		private apiTranslation: ManagementTranslationControllerService,
		private uploadManagement: UploadManagementService,
		public utils: UtilsService,
		private sharedService: SharedService,
		private translate: TranslateService,
		private dialog: MatDialog,
		public tagsDropAssign: TagsDropAssignService,
		private apiScreenshot: ManagementScreenshotControllerService
	) {
		this.formInitializer();

		this.sharedService.requestActionInComponent.pipe(takeUntilDestroyed()).subscribe({
			next: response => {
				if (response.componentName === 'ClipsFormComponent') {
					switch (response.action) {
						case 'RESET_FORM': {
							this.resetForm();
							break;
						}
					}
				}
			},
		});

		this.tagsDropAssign.updateTagDropzone.pipe(takeUntilDestroyed()).subscribe(response => {
			if (response.location === 'TAGZONE-FORM') {
				this.assignedTags.push.apply(this.assignedTags, response.tags);
				this.clipFormGroup.controls['tagIds'].setValue(this.assignedTags.map(tag => tag.id));
			}
		});
	}

	ngOnInit(): void {
		this.formControlWatchers();
	}

	ngAfterViewInit() {
		this.cdr.detectChanges();
	}

	@HostListener('document:keydown', ['$event'])
	handleKeyboardEvent(event: KeyboardEvent) {
		if (event.key === 'F3') {
			event.preventDefault();
			this.plugins.webClipPopulateFromClipboard = true;
			this.getDataFromClipBoard();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['itemForEdit'].currentValue) {
			this.clipFormGroup.reset();
			this.assignedTags = [];
			this.clipFormGroup.controls['tagIds'].setValue(null);
			this.selectedAuthors = [];
			this.componentStateChanged.next(0);
			this.mode = 'EDIT';
			setTimeout(() => {
				this.populateForm();
			}, 100);
		}
	}

	ngOnDestroy(): void {
		this.onDestroy.next('');
	}

	public onMediumAutocompleteSelected(event: MatAutocompleteSelectedEvent): void {
		const selectedMedium = event.option.value as ManagementMediumResponse;
		if (selectedMedium) {
			this.onMediumSelected(selectedMedium);
		}
	}

	private onMediumSelected(medium: ManagementMediumResponse): void {
		if (medium && medium.lang) {
			const languageMap: Record<string, string> = {
				'MK': 'mk',
				'AL': 'al', 
				'EN': 'en'
			};
			
			const targetLanguage = languageMap[medium.lang];
			if (targetLanguage && this.avaliableLanguagesCodes.includes(targetLanguage)) {
				// Switch the form language section to the medium's language
				this.switchFormLanguage(targetLanguage);
			}
		}
	}

	private switchFormLanguage(languageCode: string): void {
		// Get the index of the target language for the tab
		const languageIndex = this.avaliableLanguagesCodes.indexOf(languageCode);
		if (languageIndex !== -1 && languageIndex !== this.selectedTabIndex) {
			// Switch to the appropriate language tab/section
			this.selectedTabIndex = languageIndex;
			
			// Focus on the first form field in the new language section
			setTimeout(() => {
				const titleFieldName = 'title' + this.utils.titleCase(languageCode);
				const titleControl = this.clipFormGroup.controls[titleFieldName];
				if (titleControl) {
					// Trigger change detection and focus if needed
					this.cdr.detectChanges();
				}
			}, 100);
		}
	}

	/** FORMS initializations, events, validation */
	private formInitializer() {
		this.clipFormGroup = this.fb.group({
			medium: [],
			publicationDate: [new Date(), Validators.required],
			author: [],
			type: [null],
			hasVideo: [false],
			// keywords: [],
			important: [false],
			mediaUrl: [],
			videoStartSeconds: [{ value: '', disabled: true }, [Validators.pattern(/^\d+:[0-5]\d$/)]],
			videoEndSeconds: [{ value: '', disabled: true }, [Validators.pattern(/^\d+:[0-5]\d$/)]],
			url: [''],
			screenshotUrl: [''],
			tagIds: [],
			area: [3],
			pageNumber: [],
			sif: [{ value: '', disabled: true }],

			printedOptionsCover: ['notCover'],
			printedOptionsImage: ['withoutImage'],
			printedOptionsResearch: ['own'],
			printedOptionsRelevancy: ['narower_area'],

			transcription: [false],
		});

		this.avaliableLanguagesCodes.forEach(code => {
			let componentName = 'id' + this.utils.titleCase(code);
			let control: FormControl = new FormControl();
			this.clipFormGroup.addControl(componentName, control);

			componentName = 'title' + this.utils.titleCase(code);
			control = new FormControl();
			this.clipFormGroup.addControl(componentName, control);

			componentName = 'text' + this.utils.titleCase(code);
			control = new FormControl();
			this.clipFormGroup.addControl(componentName, control);

			componentName = 'brief' + this.utils.titleCase(code);
			control = new FormControl();
			this.clipFormGroup.addControl(componentName, control);
		});

		this.clusterFormGroup = this.fb.group({
			clusterId: [, [Validators.required, Validators.pattern(this.utils.VALIDATION_PATTERNS.get('URL') || '')]],
		});
	}

	private populateForm() {
		if (this.itemForEdit) {
			this.clipFormGroup.patchValue(this.itemForEdit);
			let detailsByLang;
			this.avaliableLanguagesCodes.forEach((code, index) => {
				if (this.itemForEdit) {
					detailsByLang = this.itemForEdit.details?.find(item => item.lang?.toLowerCase() === code);
					if (detailsByLang) {
						this.selectedTabIndex = index;
						this.clipFormGroup.controls['id' + this.utils.titleCase(code)].setValue(detailsByLang.id);
						this.clipFormGroup.controls['title' + this.utils.titleCase(code)].setValue(detailsByLang.title);
						this.clipFormGroup.controls['text' + this.utils.titleCase(code)].setValue(detailsByLang.text);
						this.clipFormGroup.controls['brief' + this.utils.titleCase(code)].setValue(detailsByLang.brief);
					}
				}
			});

			// Auto-switch language based on the medium's language when editing
			if (this.itemForEdit.medium) {
				this.onMediumSelected(this.itemForEdit.medium);
			}

			this.selectedAuthors = this.itemForEdit.authors || [];
			this.clipFormGroup.controls['tagIds'].setValue(this.itemForEdit.tags?.map((tag: ManagementTagResponse) => tag.id));
			if (this.itemForEdit.tags) {
				this.assignedTags.push.apply(this.assignedTags, this.itemForEdit.tags);
			}

			// TEMP: the generated api-clipping SDK has not been regenerated with videoStartSeconds/
			// videoEndSeconds on ManagementClipResponse yet, even though the backend response
			// already carries them. Read them off the raw item via a cast until it's regenerated.
			if (this.itemForEdit.hasVideo) {
				const rawItem = this.itemForEdit as unknown as { videoStartSeconds?: number | null; videoEndSeconds?: number | null };
				this.clipFormGroup.controls['videoStartSeconds'].setValue(this.formatVideoTime(rawItem.videoStartSeconds));
				this.clipFormGroup.controls['videoEndSeconds'].setValue(this.formatVideoTime(rawItem.videoEndSeconds));
			}
		}
	}

	private formControlWatchers() {
		if (this.clipFormGroup.controls['medium']) {
			this.clipFormGroup.controls['medium'].valueChanges.pipe(startWith(''), debounceTime(450), distinctUntilChanged(), takeUntil(this.onDestroy)).subscribe(searchTerm => {
				this.itemAction('SEARCH', 'MEDIUM', searchTerm);
				// Handle medium selection for language switching
				if (searchTerm && typeof searchTerm === 'object' && searchTerm.lang) {
					this.onMediumSelected(searchTerm);
				}
			});

			this.clipFormGroup.controls['author'].valueChanges.pipe(startWith(''), debounceTime(300), distinctUntilChanged(), takeUntil(this.onDestroy)).subscribe(searchTerm => {
				this.itemAction('SEARCH', 'AUTHOR', searchTerm);
			});

			this.clipFormGroup.controls['hasVideo'].valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(hasVideo => {
				const startControl = this.clipFormGroup.controls['videoStartSeconds'];
				const endControl = this.clipFormGroup.controls['videoEndSeconds'];
				if (hasVideo) {
					startControl.enable({ emitEvent: false });
					endControl.enable({ emitEvent: false });
				} else {
					startControl.setValue('', { emitEvent: false });
					endControl.setValue('', { emitEvent: false });
					startControl.disable({ emitEvent: false });
					endControl.disable({ emitEvent: false });
				}
			});

			// if (this.mode === 'NEW') {
			this.avaliableLanguagesCodes.forEach(code => {
				this.clipFormGroup.controls['text' + this.utils.titleCase(code)].valueChanges
					.pipe(
						startWith(''),
						distinctUntilChanged(),
						takeUntil(this.onDestroy)
						// takeUntil(this.componentStateChanged)
					)
					.subscribe(text => {
						let textModification = text ? text.replace(/^\s*\n/gm, '\n') : null; // remove multiple empty lines
						if (textModification) {
							textModification = textModification.replace(
								/([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
								''
							); // remove emojis
							this.clipFormGroup.controls['text' + this.utils.titleCase(code)].setValue(textModification);

							if (textModification) {
								let briefText = textModification;
								const maxLength = 700;

								if (textModification.length > maxLength) {
									const lastPeriodIndex = textModification.lastIndexOf('.', maxLength);

									if (lastPeriodIndex !== -1) {
										briefText = textModification.substring(0, lastPeriodIndex + 1);
									} else {
										briefText = textModification.substring(0, maxLength);
									}
								}

								this.clipFormGroup.controls['brief' + this.utils.titleCase(code)].setValue(briefText);
							}
						}
					});
			});
			// }
		}
	}

	/* this is workaround for known material bug
    https://github.com/angular/components/issues/14291 */
	dateInput($event: any) {
		const dateSplited = $event.target.value.split('/');
		const day = parseInt(dateSplited[0], 10);
		const month = parseInt(dateSplited[1], 10) - 1;
		const year = parseInt(dateSplited[2], 10);
		const parsedDate = new Date(year, month, day);
		this.clipFormGroup.controls['publicationDate'].setValue(parsedDate);
	}

	formatVideoTime(totalSeconds: number | null | undefined): string {
		if (totalSeconds === null || totalSeconds === undefined) {
			return '';
		}
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = Math.floor(totalSeconds % 60);
		return `${minutes}:${seconds.toString().padStart(2, '0')}`;
	}

	private parseVideoTime(value: string | null | undefined): number | null {
		const match = (value || '').trim().match(/^(\d+):([0-5]\d)$/);
		if (!match) {
			return null;
		}
		return Number(match[1]) * 60 + Number(match[2]);
	}

	// Ѓ2: true only for a TV/RADIO clip that actually has a video to position/preview - same
	// gate the "AUTO_TRANSCRIPTION" checkbox already uses for medium type.
	get showBoundaryPlayer(): boolean {
		const mediumType = this.clipFormGroup.controls['medium'].value?.type;
		return !!this.clipFormGroup.controls['hasVideo'].value && !!this.clipFormGroup.controls['mediaUrl'].value && (mediumType === 'TV' || mediumType === 'RADIO');
	}

	// Seeks to the clip's own start boundary as soon as the edition video is ready, so the
	// editor immediately sees the right frame instead of frame 0 of a potentially long edition.
	onBoundaryPlayerLoadedMetadata(): void {
		const video = this.boundaryPlayerRef?.nativeElement;
		const start = this.parseVideoTime(this.clipFormGroup.controls['videoStartSeconds'].value);
		if (video && start !== null) {
			video.currentTime = start;
		}
		this.boundaryPlayerAutoStopped = false;
	}

	onBoundaryPlayerTimeUpdate(): void {
		const video = this.boundaryPlayerRef?.nativeElement;
		if (!video || this.boundaryPlayerAutoStopped) {
			return;
		}
		const end = this.parseVideoTime(this.clipFormGroup.controls['videoEndSeconds'].value);
		if (end !== null && video.currentTime >= end) {
			video.pause();
			this.boundaryPlayerAutoStopped = true;
		}
	}

	onBoundaryPlayerSeeked(): void {
		const video = this.boundaryPlayerRef?.nativeElement;
		const end = this.parseVideoTime(this.clipFormGroup.controls['videoEndSeconds'].value);
		if (video && end !== null) {
			this.boundaryPlayerAutoStopped = video.currentTime >= end;
		}
	}

	setVideoStartFromPlayer(): void {
		const video = this.boundaryPlayerRef?.nativeElement;
		if (video) {
			this.clipFormGroup.controls['videoStartSeconds'].setValue(this.formatVideoTime(video.currentTime));
		}
	}

	setVideoEndFromPlayer(): void {
		const video = this.boundaryPlayerRef?.nativeElement;
		if (video) {
			this.clipFormGroup.controls['videoEndSeconds'].setValue(this.formatVideoTime(video.currentTime));
			this.boundaryPlayerAutoStopped = false;
		}
	}

	checkFormValidity() {
		let validity = false;
		this.avaliableLanguagesCodes.some(code => {
			return (validity =
				this.clipFormGroup.controls['title' + this.utils.titleCase(code)].value &&
				(this.clipFormGroup.controls['transcription'].value || // if transcription is checked, text and brief are not required
					(this.clipFormGroup.controls['text' + this.utils.titleCase(code)].value && this.clipFormGroup.controls['brief' + this.utils.titleCase(code)].value)));
		});

		if (validity) {
			const medium = this.clipFormGroup.controls['medium'].value;
			if (medium && medium.type === 'PRINTED') {
				this.clipFormGroup.controls['pageNumber'].addValidators([Validators.required, Validators.pattern(this.utils.VALIDATION_PATTERNS.get('NUMBER_LIST') || '')]);
				this.clipFormGroup.controls['pageNumber'].updateValueAndValidity();
				validity = this.clipFormGroup.controls['pageNumber'].valid;
				if (!validity) {
					return validity;
				}
			} else {
				this.clipFormGroup.controls['pageNumber'].removeValidators(Validators.required);
				this.clipFormGroup.controls['pageNumber'].updateValueAndValidity();
			}

			if (medium && (medium.type === 'TV' || medium.type === 'RADIO' || medium.type === 'PRINTED')) {
				this.clipFormGroup.controls['mediaUrl'].addValidators(Validators.required);
				this.clipFormGroup.controls['url'].removeValidators([Validators.required, Validators.pattern(this.utils.VALIDATION_PATTERNS.get('URL') || '')]);
				validity = this.clipFormGroup.controls['mediaUrl'].valid && this.clipFormGroup.controls['mediaUrl'].value !== null;
			} else if (!medium || (medium && medium.type === 'WEB')) {
				this.clipFormGroup.controls['mediaUrl'].removeValidators(Validators.required);
				this.clipFormGroup.controls['url'].addValidators([Validators.required, Validators.pattern(this.utils.VALIDATION_PATTERNS.get('URL') || '')]);
				validity = this.clipFormGroup.controls['url'].valid;
			}
		}

		return validity;
	}

	resetForm(formDirective?: FormGroupDirective, hardReset?: boolean) {
		this.itemForEdit = {
			id: '',
			tags: [],
		};

		const storePreviousData = {
			publicationDate: this.clipFormGroup.controls['publicationDate'].value || new Date(),
			tagIds: this.assignedTags.map(tag => tag.id),
		};

		this.clipFormGroup.reset();
		if (formDirective) {
			formDirective.resetForm();
		}
		this.selectedAuthors = [];

		// set default values
		this.clipFormGroup.controls['medium'].setValue(null);
		this.clipFormGroup.controls['important'].setValue(false);
		this.clipFormGroup.controls['url'].setValue('');
		this.clipFormGroup.controls['author'].setValue(null);
		this.authorInput.nativeElement.value = '';

		if (hardReset) {
			this.clipFormGroup.controls['publicationDate'].setValue(new Date());
			this.clipFormGroup.controls['tagIds'].setValue(null);
			this.assignedTags = [];

			setTimeout(() => {
				this.selectedTabIndex = 0;
			}, 1000);
		} else {
			this.clipFormGroup.controls['tagIds'].setValue(storePreviousData.tagIds);
			this.clipFormGroup.controls['publicationDate'].setValue(storePreviousData.publicationDate);
		}

		this.clipFormGroup.controls['printedOptionsCover'].setValue('notCover');
		this.clipFormGroup.controls['printedOptionsImage'].setValue('withoutImage');
		this.clipFormGroup.controls['printedOptionsResearch'].setValue('own');
		this.clipFormGroup.controls['printedOptionsRelevancy'].setValue('narower_area');
		this.clipFormGroup.controls['area'].setValue(3);

		this.mode = 'NEW';
	}

	getDataFromClipBoard(): void {
		const activeTabLang = this.avaliableLanguagesCodes[this.selectedTabIndex];
		navigator['clipboard'].readText().then(data => {
			this.plugins.webClipPopulateFromClipboard = false;
			let indexOfNewLine = data.indexOf('\r');
			this.clipFormGroup.controls['url'].setValue(data.substring(0, indexOfNewLine));
			data = data.substring(indexOfNewLine + 2);
			indexOfNewLine = data.indexOf('\r');
			this.clipFormGroup.controls[`title${this.utils.titleCase(activeTabLang)}`].setValue(data.substring(0, indexOfNewLine + 1));
			data = data.substring(indexOfNewLine + 2);
			this.clipFormGroup.controls[`text${this.utils.titleCase(activeTabLang)}`].setValue(data);
		});
	}

	scrollToTop() {
		this.clipsFormElement.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}

	getTranslatedStatus(langCode: string) {
		if (
			this.clipFormGroup.controls['title' + this.utils.titleCase(langCode)].value &&
			this.clipFormGroup.controls['text' + this.utils.titleCase(langCode)].value &&
			this.clipFormGroup.controls['brief' + this.utils.titleCase(langCode)].value
		) {
			return true;
		}
		return false;
	}

	private openMediumManagementDialog() {
		const dialogRef = this.dialog.open(MediumComponent, {
			width: '600px',
			data: {
				mode: 'NEW',
				type: 'MEDIUM',
				typeWithScope: 'medium_clipping',
			},
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				this.filteredMediums = [];
			});
	}

	private openAuthorManagementDialog() {
		const dialogRef = this.dialog.open(AuthorComponent, {
			width: '600px',
			data: {
				mode: 'NEW',
				type: 'AUTHOR',
			},
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				this.filteredAuthors = [];
			});
	}
	/** (END)FORMS initializations, events, validation */

	removeTag(tag: ManagementTagResponse) {
		this.assignedTags = this.assignedTags.filter(item => item.id !== tag.id);
		this.clipFormGroup.controls['tagIds'].setValue(this.assignedTags.map(tag => tag.id));
	}

	pageAreaLabel(value: number): string {
		if (value === 0) {
			return '>1';
		}
		// else if (value === 1) {
		// 	return '1';
		// }
		return `1/${value}`;
	}

	displayFn(medium: ManagementMediumResponse): string {
		return medium && medium.id ? this.utils.getParameterByLanguageScript(medium, 'name') : '';
	}

	itemAction(
		action: 'SEARCH' | 'REMOVE' | 'ADD' | 'SELECT' | 'CREATE',
		type: 'AUTHOR' | 'CLUSTER' | 'MEDIUM',
		item: string | ManagementAuthorResponse | MatChipInputEvent | MatAutocompleteSelectedEvent
	) {
		switch (type) {
			case 'AUTHOR': {
				if (action === 'SEARCH') {
					if (item && (item as string).length >= 1) {
						this.apiClippingAuthor
							.searchAuthors(item as string)
							.pipe(take(1))
							.subscribe(response => {
								this.filteredAuthors = response;
								// if (response && response.length) {
								// 	this.clipFormGroup.controls['author'].setValue(response[0]);
								// }
							});
					}
				} else if (action === 'CREATE') {
					this.openAuthorManagementDialog();
				} else if (action === 'REMOVE') {
					this.selectedAuthors = this.selectedAuthors.filter(author => author.id !== (item as ManagementAuthorResponse).id);
				} else if (action === 'SELECT') {
					this.selectedAuthors.push((item as MatAutocompleteSelectedEvent).option.value);
					this.clipFormGroup.controls['author'].setValue(null);
					this.authorInput.nativeElement.value = '';
				} else if (action === 'ADD') {
					if ((item as MatChipInputEvent).value) {
						this.createAuthor((item as MatChipInputEvent).value);
					}
				}
				break;
			}
			case 'MEDIUM': {
				if (action === 'SEARCH') {
					if (item && (item as string).length >= 1) {
						this.apiClippingMedium
							.searchMediums(item as string)
							.pipe(take(1))
							.subscribe(response => {
								this.filteredMediums = response;
							});
					}
				} else if (action === 'CREATE') {
					this.openMediumManagementDialog();
				}
				break;
			}
		}
	}

	/** UPLOAD media */
	selectScreenshotForUpload(event: any) {
		this.uploadManagement.generatePresignedUrl(event.target.files[0], 'SCREENSHOT', 'CLIPPING');
		this.uploadManagement.linkGenerated.pipe(take(1)).subscribe(response => {
			this.clipFormGroup.controls['screenshotUrl'].setValue(response);
		});
	}

	selectFilesForUpload(event: any) {
		this.uploadManagement.generatePresignedUrl(event.target.files[0], this.getFileFormatType(), 'CLIPPING');
		this.uploadManagement.linkGenerated.pipe(take(1)).subscribe(response => {
			this.clipFormGroup.controls['mediaUrl'].setValue(response);
		});
	}

	getAcceptedFileFormats() {
		const medium = this.clipFormGroup.controls['medium'].value;
		if (medium && medium.type === 'PRINTED') {
			return ['image/*'];
		} else if (medium && (medium.type === 'WEB' || medium.type === 'TV' || medium.type === 'RADIO')) {
			return ['audio/*', 'video/*'];
		} else {
			return ['image/*'];
		}
		return [];
	}

	getFileFormatType() {
		const medium = this.clipFormGroup.controls['medium'].value;
		if (medium && medium.type === 'PRINTED') {
			return 'IMAGE';
		} else if (medium && (medium.type === 'WEB' || medium.type === 'TV' || medium.type === 'RADIO')) {
			return 'VIDEO';
		}
		return 'IMAGE';
	}
	/** (END)UPLOAD media */

	/** CREATE/EDIT Clip */
	submitClipsForm(formDirective: FormGroupDirective, event: Event) {
		event.stopPropagation();
		event.preventDefault();
		this.apiLoading.clip = true;

		const medium = this.clipFormGroup.controls['medium'].value;

		const clipDto: CreateClipRequest | UpdateClipRequest = {
			url: this.clipFormGroup.controls['url'].value,
			mediaUrl: this.clipFormGroup.controls['mediaUrl'].value,
			screenshotUrl: this.clipFormGroup.controls['screenshotUrl'].value,
			publicationDate: this.clipFormGroup.controls['publicationDate'].value,
			important: this.clipFormGroup.controls['important'].value,
			mediumId: medium ? medium.id : null,
			tagIds: this.clipFormGroup.controls['tagIds'].value,
			authorIds: this.selectedAuthors.map(author => author.id),
			type: this.clipFormGroup.controls['type'].value,
			hasVideo: this.clipFormGroup.controls['hasVideo'].value,
			details: this.avaliableLanguagesCodes
				.map(langCode => ({
					id: this.clipFormGroup.controls['id' + this.utils.titleCase(langCode)].value,
					title: this.clipFormGroup.controls['title' + this.utils.titleCase(langCode)].value,
					text: this.clipFormGroup.controls['text' + this.utils.titleCase(langCode)].value,
					brief: this.clipFormGroup.controls['brief' + this.utils.titleCase(langCode)].value,
					lang: (<any>CreateCategoryDetailsRequest.LangEnum)[langCode.toUpperCase()],
				}))
				.filter(details => details.title || details.text || details.brief),
			area: this.clipFormGroup.controls['area'].value,
			pageNumber: medium && medium.type === 'PRINTED' ? this.clipFormGroup.controls['pageNumber'].value : undefined,
			sif: medium && medium.type === 'PRINTED' ? this.calculateSif() : undefined,
      		transcribed: this.clipFormGroup.controls['transcription'].value,
		};

		// TEMP: the generated api-clipping SDK has not been regenerated with videoStartSeconds/
		// videoEndSeconds on CreateClipRequest/UpdateClipRequest yet, even though the backend
		// already accepts them. Attach them via a cast until it's regenerated.
		const clipDtoWithVideoBoundaries = clipDto as CreateClipRequest & { videoStartSeconds?: number | null; videoEndSeconds?: number | null };
		const hasVideo = this.clipFormGroup.controls['hasVideo'].value;
		clipDtoWithVideoBoundaries.videoStartSeconds = hasVideo ? this.parseVideoTime(this.clipFormGroup.controls['videoStartSeconds'].value) : null;
		clipDtoWithVideoBoundaries.videoEndSeconds = hasVideo ? this.parseVideoTime(this.clipFormGroup.controls['videoEndSeconds'].value) : null;

		if (this.mode === 'NEW') {
			this.apiClippingClips
				.createClip(clipDto)
				.pipe(take(1))
				.subscribe({
					next: response => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
							type: 'SUCCESS',
						});

						if (clipDto.screenshotUrl) {
							this.uploadManagement.uploadFile(response);
						}

						if (medium && medium.type !== 'WEB' && this.clipFormGroup.controls['mediaUrl'].value) {
							const currentLang = this.avaliableLanguagesCodes[this.selectedTabIndex].toUpperCase();
							this.uploadManagement.uploadFile(response, this.clipFormGroup.controls['transcription'].value, currentLang);
						}
						this.resetForm(formDirective);
						// this.actionRequested.emit({ action: 'LOAD', data: 'list', id: 0 });
						this.actionRequested.emit({
							action: 'TOOLBAR',
							groupAction: 'MERGE_GROUP',
							item: response,
						});
						this.apiLoading.clip = false;
					},
					error: err => {
						this.sharedService.showNotification.next({
							message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
							type: 'ERROR',
						});
						this.apiLoading.clip = false;
					},
				});
		} else if (this.itemForEdit) {
			const recalulateSif =
				this.clipFormGroup.controls['pageNumber'].dirty ||
				this.clipFormGroup.controls['area'].dirty ||
				this.clipFormGroup.controls['printedOptionsCover'].dirty ||
				this.clipFormGroup.controls['printedOptionsImage'].dirty ||
				this.clipFormGroup.controls['printedOptionsResearch'].dirty ||
				this.clipFormGroup.controls['printedOptionsRelevancy'].dirty;

			clipDto.sif = recalulateSif ? this.calculateSif() : this.clipFormGroup.controls['sif'].value;

			// The form has no visibility index control, but updateClip overwrites the stored value
			// with whatever the request carries — omitting it nulls the index on every save.
			clipDto.visibilityIndex = this.itemForEdit.visibilityIndex;

			this.apiClippingClips
				.updateClip(clipDto as UpdateClipRequest, this.itemForEdit.id)
				.pipe(take(1))
				.subscribe({
					next: () => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
							type: 'SUCCESS',
						});

						if (this.itemForEdit && clipDto.screenshotUrl !== this.itemForEdit.screenshotUrl) {
							this.uploadManagement.uploadFile(this.itemForEdit);
						}

						if (this.itemForEdit && this.clipFormGroup.controls['mediaUrl'].value !== this.itemForEdit.mediaUrl) {
							const currentLang = this.avaliableLanguagesCodes[this.selectedTabIndex].toUpperCase();
							this.uploadManagement.uploadFile(this.itemForEdit, this.clipFormGroup.controls['transcription'].value, currentLang);
						} else if (this.clipFormGroup.controls['transcription'].value && this.itemForEdit) {
							const currentLang = this.avaliableLanguagesCodes[this.selectedTabIndex].toUpperCase();
							this.uploadManagement.sendUploadCompleteRequest(this.itemForEdit, currentLang);
						}
						if (this.itemForEdit) {
							this.actionRequested.emit({
								action: 'LOAD',
								data: 'filterCheck',
								id: 'tag-dropzone-list-' + (this.itemForEdit.main ? this.itemForEdit.groupId : this.itemForEdit.id),
							});
						}
						this.resetForm(formDirective);
						this.apiLoading.clip = false;
						this.mode = 'NEW';
					},
					error: err => {
						this.sharedService.showNotification.next({
							message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
							type: 'ERROR',
						});
						this.apiLoading.clip = false;
					},
				});
		}
	}
	/** (END) CREATE/EDIT Clip */

	/** SIF caluclation */
	calculateSif() {
		let sif = 0;
		const SIF_DATA: Map<string, number> = new Map([
			['isCover', 1],
			['notCover', 0],
			['withImage', 1.5],
			['withoutImage', 1],
			['own', 1],
			['taken', 0.5],
			['transferred', 0.3],
			['mentioned_client', 2],
			['narower_area', 1],
			['wider_area', 0],
			['0', 2],
			['1', 1.8],
			['2', 1.5],
			['3', 1.3],
			['4', 1.2],
			['5', 1],
			['6', 0.8],
			['7', 0.7],
			['8', 0.6],
			['9', 0.5],
			['10', 0.5],
			['11', 0.4],
			['12', 0.3],
		]);

		sif += SIF_DATA.get(this.clipFormGroup.controls['printedOptionsCover'].value) || 0;
		sif += SIF_DATA.get(this.clipFormGroup.controls['printedOptionsImage'].value) || 0;
		sif += SIF_DATA.get(this.clipFormGroup.controls['printedOptionsResearch'].value) || 0;
		sif += SIF_DATA.get(this.clipFormGroup.controls['printedOptionsRelevancy'].value) || 0;
		sif += SIF_DATA.get(this.clipFormGroup.controls['area'].value.toString()) || 0;

		if (this.selectedAuthors.length > 0 && this.selectedAuthors.length < 6) {
			sif += 0.5;
		} else if (this.selectedAuthors.length > 6) {
			sif += 1;
		}

		let pageNumber = this.clipFormGroup.controls['pageNumber'].value.split(',')[0];
		if (pageNumber < 11) {
			sif += 1.5;
		} else if (pageNumber < 21) {
			sif += 1.3;
		} else {
			sif += 1;
		}

		return sif;
	}
	/** (END)SIF caluclation */

	/** Cluster methods   */
	createCluster(formDirective: FormGroupDirective, event: Event) {
		event.stopPropagation();
		event.preventDefault();
		this.apiLoading.cluster = true;

		this.clusterFormGroup.controls['clusterId'].setValue(this.clusterFormGroup.controls['clusterId'].value.replace('www.', ''));

		const cluster = {
			id: '',
			type: Constants.CLUSTER_TYPES.find(clusterType => this.clusterFormGroup.controls['clusterId'].value.includes(clusterType.url)),
		};

		if (cluster.type) {
			switch (cluster.type.id) {
				case 'GRID':
				case 'FAXAL': {
					cluster.id = this.clusterFormGroup.controls['clusterId'].value;
					cluster.id = cluster.id.replaceAll(cluster.type.url, '');
					cluster.id = cluster.id.substring(0, cluster.id.indexOf('/'));

					this.apiClippingClips
						.saveClipsFromCluster(cluster.type.id, cluster.id)
						.pipe(take(1))
						.subscribe({
							next: () => {
								this.sharedService.showNotification.next({
									message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
									type: 'SUCCESS',
								});
								this.clusterFormGroup.reset();
								formDirective.resetForm();
								this.actionRequested.emit({ action: 'LOAD', data: 'list', id: 0 });
								this.apiLoading.cluster = false;
							},
							error: err => {
								this.sharedService.showNotification.next({
									message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
									type: 'ERROR',
								});
								this.apiLoading.cluster = false;
							},
						});
					break;
				}
				case 'VESTI': {
					cluster.id = this.clusterFormGroup.controls['clusterId'].value;
					cluster.id = cluster.id.replaceAll(cluster.type.url, '');
					cluster.id = cluster.id.substring(0, cluster.id.indexOf('-'));

					this.apiClippingClips
						.saveClipsFromCluster(cluster.type.id, cluster.id)
						.pipe(take(1))
						.subscribe({
							next: () => {
								this.sharedService.showNotification.next({
									message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
									type: 'SUCCESS',
								});
								this.clusterFormGroup.reset();
								formDirective.resetForm();
								this.actionRequested.emit({ action: 'LOAD', data: 'list', id: 0 });
								this.apiLoading.cluster = false;
							},
							error: err => {
								this.sharedService.showNotification.next({
									message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
									type: 'ERROR',
								});
								this.apiLoading.cluster = false;
							},
						});
					break;
				}
				case 'GRID_SEARCH': {
					const params = this.utils.getQueryParamsFromStringUrl(this.clusterFormGroup.controls['clusterId'].value);
					if (params.q && params.beg && params.end) {
						let dateFrom = params.beg.split('.');
						dateFrom = new Date(`${dateFrom[2]}-${dateFrom[1]}-${dateFrom[0]}`);
						let dateTo = params.end.split('.');
						dateTo = new Date(`${dateTo[2]}-${dateTo[1]}-${dateTo[0]}`);
						this.apiClippingClips
							.searchGridForText(params.q, {
								sources: ['GRID'],
								dateFrom,
								dateTo,
							})
							.pipe(take(1))
							.subscribe({
								next: () => {
									this.sharedService.showNotification.next({
										message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
										type: 'SUCCESS',
									});
									this.clusterFormGroup.reset();
									formDirective.resetForm();
									this.actionRequested.emit({ action: 'LOAD', data: 'list', id: 0 });
									this.apiLoading.cluster = false;
								},
								error: err => {
									this.sharedService.showNotification.next({
										message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
										type: 'ERROR',
									});
									this.apiLoading.cluster = false;
								},
							});
					}
					break;
				}
				case 'VESTI_SEARCH': {
					const params = this.utils.getQueryParamsFromStringUrl(this.clusterFormGroup.controls['clusterId'].value);
					const query = params.q ? params.q.trim() : '';
					if (!query) {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR') + '\n' + 'Недостасува пребаран текст (q) во линкот.',
							type: 'ERROR',
						});
						this.apiLoading.cluster = false;
						break;
					}
					if (query.length > 200) {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR') + '\n' + 'Пребараниот текст е премногу долг.',
							type: 'ERROR',
						});
						this.apiLoading.cluster = false;
						break;
					}

					this.apiClippingClips
						.searchVestiForText(query)
						.pipe(take(1))
						.subscribe({
							next: () => {
								this.sharedService.showNotification.next({
									message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
									type: 'SUCCESS',
								});
								this.clusterFormGroup.reset();
								formDirective.resetForm();
								this.actionRequested.emit({ action: 'LOAD', data: 'list', id: 0 });
								this.apiLoading.cluster = false;
							},
							error: err => {
								this.sharedService.showNotification.next({
									message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
									type: 'ERROR',
								});
								this.apiLoading.cluster = false;
							},
						});
					break;
				}
				case 'GRID_SOURCE': {
					cluster.id = this.clusterFormGroup.controls['clusterId'].value;
					cluster.id = cluster.id.replaceAll(cluster.type.url, '');

					this.apiClippingClips
						.searchGridSources(cluster.id)
						.pipe(take(1))
						.subscribe({
							next: () => {
								this.sharedService.showNotification.next({
									message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
									type: 'SUCCESS',
								});
								this.clusterFormGroup.reset();
								formDirective.resetForm();
								this.actionRequested.emit({ action: 'LOAD', data: 'list', id: 0 });
								this.apiLoading.cluster = false;
							},
							error: err => {
								this.sharedService.showNotification.next({
									message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
									type: 'ERROR',
								});
								this.apiLoading.cluster = false;
							},
						});
					break;
				}
				case 'FAXAL_SOURCE': {
					cluster.id = this.clusterFormGroup.controls['clusterId'].value;
					cluster.id = cluster.id.replaceAll(cluster.type.url, '');
					const params = cluster.id.split('/');
					this.apiClippingClips
						.scrapeFaxalSources(parseInt(params[0], 10), params[1])
						.pipe(take(1))
						.subscribe({
							next: () => {
								this.sharedService.showNotification.next({
									message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
									type: 'SUCCESS',
								});
								this.clusterFormGroup.reset();
								formDirective.resetForm();
								this.actionRequested.emit({ action: 'LOAD', data: 'list', id: 0 });
								this.apiLoading.cluster = false;
							},
							error: err => {
								this.sharedService.showNotification.next({
									message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
									type: 'ERROR',
								});
								this.apiLoading.cluster = false;
							},
						});

					break;
				}
			}
		} else {
			this.apiLoading.cluster = false;
			this.sharedService.showNotification.next({
				message: this.translate.instant('NOTIFICATIONS.UNSUPPORTED_CLUSTER_LINK'),
				type: 'WARNING',
			});
		}
	}
	/** (END)Cluster methods   */

	/** Author methods   */
	private createAuthor(name: string) {
		this.apiClippingAuthor
			.createAuthor({
				nameCyrillic: name,
				nameLatin: this.utils.transliterate('CYR_LAT', name),
			})
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
						type: 'SUCCESS',
					});
					this.selectedAuthors.push(response);
					this.clipFormGroup.controls['author'].setValue(null);
					this.authorInput.nativeElement.value = '';
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}
	/** (END)Author methods */

	openScreenshotQueue() {
		this.dialog.open(ScreenshotQueueDialogComponent, {
			width: '700px',
			data: {
				apiService: this.apiScreenshot,
			},
		});
	}

	importClips() {
		const input = document.createElement('input');
		input.type = 'file';
		input.accept = '.csv';
		input.onchange = (event: any) => {
			const file = event.target.files[0];
			if (file) {
				this.showImportConfirmation(file);
			}
		};
		input.click();
	}

	showTranslateClipsByTagsConfirmation() {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '500px',
			data: {
				mode: 'USER_ACTION',
				type: 'message',
				message: this.translate.instant('NOTIFICATIONS.TRANSLATE_CONFIRMATION')
			},
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				if (result) {
					this.translateClipsByTags();
				}
			});
	}

	private translateClipsByTags() {
		this.apiLoading.translateClipsByTag = true;

		this.apiClippingClips.translateMainClipsWithTranslatableTags()
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.ACTION_COMPLETE'),
						type: 'SUCCESS',
					});
					this.apiLoading.translateClipsByTag = false;
				},
				error: (err) => {
					this.sharedService.showNotification.next({
						message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error?.message || err.message}`,
						type: 'ERROR',
					});
					this.apiLoading.translateClipsByTag = false;
				},
			});
	}

	private showImportConfirmation(file: File) {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '500px',
			data: {
				mode: 'USER_ACTION',
				type: 'message',
				message: this.translate.instant('CLIPS.IMPORT_CLIPS_CONFIRMATION', { fileName: file.name })
			},
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				if (result) {
					this.uploadCSVFile(file);
				}
			});
	}

	private uploadCSVFile(file: File) {
		this.apiLoading.importClips = true;
		
		this.apiClippingClips.importClipsForm(file)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
						type: 'SUCCESS',
					});
					this.actionRequested.emit({ action: 'LOAD', data: 'list', id: 0 });
					this.apiLoading.importClips = false;
				},
				error: (err) => {
					this.sharedService.showNotification.next({
						message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error?.message || err.message}`,
						type: 'ERROR',
					});
					this.apiLoading.importClips = false;
				},
			});
	}

	openTools() {
		const dialogRef = this.dialog.open(ClipsToolsComponent, {
			width: '600px',
			data: {
				mode: 'USER_ACTION',
				type: 'custom-fields',
				fields: [
					{
						type: 'date-range',
						value: [new Date(), new Date()],
					},
					{
						type: 'selection-multiple',
						value: Constants.KEYWORDS_SOURCE_TYPES,
					},
					{
						type: 'medium-selection',
						value: 'autocomplete',
					},
					{
						type: 'tag-selection',
						value: [],
					},
				],
			},
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				if (result) {
					switch (result.data.tool) {
						case 'MERGE_SIMILAR': {
							this.mergeBySimilarity(result.data.dateFrom, result.data.dateTo, result.data.tags);
							break;
						}
						case 'AUTO_TAG': {
							this.autoTagClips(result.data.dateFrom, result.data.dateTo, result.data.tags);
							break;
						}
						case 'PDF_BY_TAG': {
							this.reportByTag(result.data.dateFrom, result.data.dateTo, result.data.medium);
							break;
						}
					}
				}
			});
	}

	/** Search for keywords methods */
	public searchForKeywordsButton(event: string) {
		this.apiLoading.searchKeywords = true;
		// if (event === 'MAIN') {
		// 	this.searchForKeywords();
		// } else if (event === 'SECONDARY') {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '600px',
			data: {
				mode: 'USER_ACTION',
				type: 'custom-fields',
				fields: [
					{
						type: 'date-range',
						data: [
							{
								name: '',
								value: [new Date(), new Date()],
							},
						],
					},
					{
						type: 'selection-multiple',
						data: Constants.KEYWORDS_SOURCE_TYPES.map(kw => ({
							name: kw,
							value: kw,
						})),
					},
				],
			},
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				if (result) {
					this.searchForKeywords(result.data.dateFrom, result.data.dateTo, result.data.selection);
				} else {
					this.apiLoading.searchKeywords = false;
				}
			});
		// }
	}

	private searchForKeywords(dateFrom?: Date, dateTo?: Date, sources?: SearchRequest.SourcesEnum[]) {
		dateFrom = dateFrom ? dateFrom : new Date();
		dateTo = dateTo ? dateTo : new Date();

		const searchRequest: SearchRequest = {
			sources: sources || [],
			dateFrom,
			dateTo,
		};

		this.apiClippingClips
			.searchNewsSourceForAllKeywords(searchRequest)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.ACTION_COMPLETE'),
						type: 'SUCCESS',
					});
					this.apiLoading.searchKeywords = false;
					this.actionRequested.emit({ action: 'LOAD', data: 'list', id: 0 });
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.ACTION_ERROR'),
						type: 'ERROR',
					});
					this.apiLoading.searchKeywords = false;
				},
			});
	}
	/** (END)Search for keywords methods */

	/** Translation module methods */
	translateClipText(langCode: string) {
		this.apiLoading.translate = true;
		if (!this.getTranslatedStatus(langCode) && this.itemForEdit) {
			const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
				width: '600px',
				data: {
					type: 'translate-confirmation',
					mode: 'CONFIRM',
				},
			});

			dialogRef
				.afterClosed()
				.pipe(take(1))
				.subscribe(result => {
					if (result && this.itemForEdit) {
						this.apiTranslation
							.translateClipMgmt(this.itemForEdit.id, langCode.toUpperCase())
							.pipe(take(1))
							.subscribe({
								next: (response: ManagementClipResponse) => {
									this.itemForEdit = response;
									this.clipFormGroup.reset();
									this.selectedAuthors = [];
									this.componentStateChanged.next(0);
									this.mode = 'EDIT';
									this.populateForm();
									this.apiLoading.translate = false;
								},
							});

						this.resetForm();
					}
				});
		}
	}
	/** (END)Translation module methods */

	/** Merge by title methods */
	// public mergeBySimilarityButton(event: string) {
	// 	this.apiLoading.mergeBySimilarity = true;
	// 	if (event === 'MAIN') {
	// 		this.mergeBySimilarity();
	// 	} else if (event === 'SECONDARY') {
	// 		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
	// 			width: '600px',
	// 			data: {
	// 				mode: 'USER_ACTION',
	// 				type: 'custom-fields',
	// 				fields: [
	// 					{
	// 						type: 'date-range',
	// 						value: [new Date(), new Date()],
	// 					},
	// 				],
	// 			},
	// 		});

	// 		dialogRef
	// 			.afterClosed()
	// 			.pipe(take(1))
	// 			.subscribe(result => {
	// 				if (result) {
	// 					this.mergeBySimilarity(result.data.dateFrom, result.data.dateTo);
	// 				} else {
	// 					this.apiLoading.mergeBySimilarity = false;
	// 				}
	// 			});
	// 	}
	// }

	private mergeBySimilarity(startDate?: Date, endDate?: Date, selectedTags?: any[]) {
		this.apiLoading.mergeBySimilarity = false;
		startDate = startDate ? startDate : new Date(new Date(new Date().setDate(new Date().getDate() - 1)).setHours(0, 0, 0));
		endDate = endDate ? endDate : new Date(new Date().setHours(23, 59, 59));

		const mergeSimilarRequest: MergeSimilarClipsRequest = {
			dateFrom: startDate,
			dateTo: endDate,
			tagIds: selectedTags && selectedTags.length > 0 ? selectedTags.map(tag => tag.id) : undefined
		};

		this.apiClippingClips
			.mergeClipsBySimilarity(mergeSimilarRequest)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.ACTION_COMPLETE'),
						type: 'SUCCESS',
					});
					this.apiLoading.mergeBySimilarity = false;
					this.actionRequested.emit({ action: 'LOAD', data: 'list', id: 0 });
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.ACTION_ERROR'),
						type: 'ERROR',
					});
					this.apiLoading.mergeBySimilarity = false;
				},
			});
	}
	/** (END)Merge by title methods */

	/** Auto tag methods */
	private autoTagClips(startDate?: Date, endDate?: Date, selectedTags?: any[]) {
		this.apiLoading.autoTag = true;
		startDate = startDate ? startDate : new Date(new Date(new Date().setDate(new Date().getDate() - 1)).setHours(0, 0, 0));
		endDate = endDate ? endDate : new Date(new Date().setHours(23, 59, 59));

		const autoTagRequest: AutoTagClipsRequest = {
			dateFrom: startDate,
			dateTo: endDate,
			tagIds: selectedTags && selectedTags.length > 0 ? selectedTags.map(tag => tag.id) : undefined
		};

		this.apiClippingClips
			.autoTagClips(autoTagRequest)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.ACTION_COMPLETE'),
						type: 'SUCCESS',
					});
					this.apiLoading.autoTag = false;
					this.actionRequested.emit({ action: 'LOAD', data: 'list', id: 0 });
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.ACTION_ERROR'),
						type: 'ERROR',
					});
					this.apiLoading.autoTag = false;
				},
			});
	}
	/** (END)Auto tag methods */

	/** Report by tag check methods */
	// public reportByTagButton(event: string) {
	// 	this.apiLoading.reportByTag = true;
	// 	// if (event === 'MAIN') {
	// 	// 	this.videoTagCheck();
	// 	// } else if (event === 'SECONDARY') {
	// 	const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
	// 		width: '600px',
	// 		data: {
	// 			mode: 'USER_ACTION',
	// 			type: 'custom-fields',
	// 			fields: [
	// 				{
	// 					type: 'date-range',
	// 					value: [new Date(), new Date()],
	// 				},
	// 			],
	// 		},
	// 	});

	// 	dialogRef
	// 		.afterClosed()
	// 		.pipe(take(1))
	// 		.subscribe(result => {
	// 			if (result) {
	// 				this.reportByTag(result.data.dateFrom, result.data.dateTo);
	// 			} else {
	// 				this.apiLoading.reportByTag = false;
	// 			}
	// 		});
	// 	// }
	// }

	private reportByTag(startDate?: Date, endDate?: Date, medium?: ManagementMediumResponse) {
		this.apiLoading.reportByTag = true;
		startDate = startDate ? startDate : new Date(new Date(new Date().setDate(new Date().getDate() - 1)).setHours(0, 0, 0));
		endDate = endDate ? endDate : new Date(new Date().setHours(23, 59, 59));

		this.apiClippingClips
			.generatePdfReportForTag({
				startDate,
				endDate,
				lang: this.utils.currentLanguage.toUpperCase() as GenerateClipReportForTagRequest.LangEnum,
				mediumIds: medium ? [medium.id] : [],
			})
			.pipe(take(1))
			.subscribe({
				next: (response: any) => {
					// this.sharedService.showNotification.next({
					// 	message: this.translate.instant('NOTIFICATIONS.ACTION_COMPLETE'),
					// 	type: 'SUCCESS',
					// });
					this.apiLoading.reportByTag = false;
					const content = new Blob([response as BlobPart], { type: 'application/pdf' });
					const fileURL = URL.createObjectURL(content);
					window.open(fileURL, '_blank');
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.ACTION_ERROR'),
						type: 'ERROR',
					});
					this.apiLoading.reportByTag = false;
				},
			});
	}
	/** (END)Report by tag methods */

	/** Upload check functions */
	uploadInProgressCheck() {
		return this.uploadManagement.filesUploading.size > 0;
	}

	showUploadStatus() {
		const uploads = [...this.uploadManagement.filesUploading.values()]
			.filter(item => !!item.url)
			.map(item => ({
				url: item.url,
				progress: item.uploadProgress,
			}));

		let uploadsHtml = '';
		uploads.forEach(file => {
			uploadsHtml += `<p>${file.url} — ${file.progress}%</p>`;
		});

		this.dialog.open(ConfirmationDialogComponent, {
			width: '600px',
			data: {
				mode: 'USER_ACTION',
				type: 'custom-fields',
				message: '',
				fields: [
					{
						type: 'rawHtml',
						data: `
                <div class="upload-status">
                  <p>${this.translate.instant('CLIPS.IN_PROGRESS')}: ${this.uploadManagement.filesUploading.size}</p>
                  <ul>${uploadsHtml}</ul>
                  <p>${this.translate.instant('CLIPS.COMPLETED')} (${this.uploadManagement.filesUploaded.length}):</p>
                  <ul>${this.uploadManagement.filesUploaded.map(file => `<li>${file.url}</li>`).join('')}</ul>
                  <p>${this.translate.instant('CLIPS.FAILED')}: ${this.uploadManagement.filesUnsuccessful.length}</p>
                  <ul>${this.uploadManagement.filesUnsuccessful.map(file => `<li>${this.describeFailedUpload(file)}</li>`).join('')}</ul>
                </div>`,
					},
				],
			},
		});
	}

	private describeFailedUpload(file: FailedUpload): string {
		const name = file.name || this.translate.instant('CLIPS.UPLOAD_FAIL_REASON.UNKNOWN_FILE');
		const reason = this.describeUploadFailureReason(file.reasonKey, file.status);
		const attempts = this.translate.instant('CLIPS.UPLOAD_FAIL_AFTER_ATTEMPTS', { attempts: file.attempts });
		return `${name} — ${reason} ${attempts}`;
	}

	private describeUploadFailureReason(reasonKey: string, status?: number): string {
		switch (reasonKey) {
			case 'NETWORK':
				return this.translate.instant('CLIPS.UPLOAD_FAIL_REASON.NETWORK');
			case 'TIMEOUT':
				return this.translate.instant('CLIPS.UPLOAD_FAIL_REASON.TIMEOUT');
			case 'EXPIRED_LINK':
				return this.translate.instant('CLIPS.UPLOAD_FAIL_REASON.EXPIRED_LINK');
			case 'SERVER_BUSY':
				return this.translate.instant('CLIPS.UPLOAD_FAIL_REASON.SERVER_BUSY', { status });
			case 'CLIENT_ERROR':
				return this.translate.instant('CLIPS.UPLOAD_FAIL_REASON.CLIENT_ERROR', { status });
			default:
				return this.translate.instant('CLIPS.UPLOAD_FAIL_REASON.UNKNOWN');
		}
	}
	/**(END) Upload check functions */
}
